import { Quiz } from './model/Quiz';
import { View as ViewDom } from './view/dom/View';
import { View as View3d } from './view/three/View';
import data from '../data/basic';
import { QuizActionEvent, QuizActionType } from './constants/QuizActionEvent';
import { shareFunc, updateShareOptions } from './share';
import { analyticsEvent } from './analytics';

export class QuizMain {
    #model;
    #viewDom;
    #view3d;

    constructor() {
        const model = new Quiz(data.quiz);
        const viewDom = new ViewDom();
        const view3d = new View3d(
            document.getElementById('canvas'),
            document.getElementById('uiMobileContainer'),
            {
                title: data.title,
                text: data.lead,
                decorationKey: data.decorationKey,
                buttonLabel: data.quiz.start
            }
        );

        model.reset();

        this.#model = model;
        this.#viewDom = viewDom;
        this.#view3d = view3d;

        this.#updateViewData();
    }

    async start(progressUpdate) {
        await this.#view3d.load(progressUpdate);

        this.#addViewListeners(this.#viewDom, true);
        this.#addViewListeners(this.#view3d);
        this.#viewDom.start();
        this.#view3d.start();
    }

    #addViewListeners(view, needChangeListener) {
        this._handleAction = (evt) => { this.#handleAction(evt); };
        this._passThroughChange = (evt) => { this.#passThroughChange(evt); };
        view.addEventListener(QuizActionEvent, this._handleAction);
        if (needChangeListener) view.addEventListener('change', this._passThroughChange);
    }

    #passThroughChange(evt) {
        this.#view3d.handleDomChange(evt);
    }

    #handleAction(evt) {
        const { action } = evt;

        if (!action) return;

        let isReset = false;

        if (action.type === QuizActionType.RESTART) {
            this.#model.reset();
            isReset = true;
        }

        const {
            currentQuestionIndex: initialIndex, numQuestions
        } = this.#model;

        // eslint-disable-next-line default-case
        switch (action.type) {
            case QuizActionType.START: {
                this.#viewDom.hideStart();
                this.#view3d.hideStart();
                this.#viewDom.showScore();
                this.#view3d.showScore();
                this.#showCurrentQuestion(initialIndex, numQuestions, 1);

                analyticsEvent('Quiz', 'Click', 'Start');

                break;
            }
            case QuizActionType.RESTART: {
                this.#updateShare({});
                this.#viewDom.hideResult(-1);
                this.#view3d.hideResult(-1);
                this.#viewDom.showScore();
                this.#view3d.showScore();
                this.#showCurrentQuestion(initialIndex, numQuestions, 1);

                analyticsEvent('Quiz', 'Click', 'Restart');

                break;
            }
            case QuizActionType.ANSWER: {
                const { questionId, value } = action;

                this.#model.answer(questionId, value);

                if (this.#model.error) {
                    console.warn(this.#model.errorMessage);

                    break;
                }

                const {
                    currentIncrement, currentComment, currentQuestionIndex, currentAnswerStatus, isTheLastQuestion
                } = this.#model;

                // this.#viewDom.displayAnswerStatus(currentAnswerStatus);
                this.#viewDom.disableOptions();
                this.#view3d.disableOptions();
                this.#view3d.displayAnswerStatus(currentAnswerStatus);

                const answerOpn = currentAnswerStatus.filter((opn) => opn.isAnswer);

                if (answerOpn && answerOpn.length > 0) {
                    const { id, label } = answerOpn[0];

                    analyticsEvent(
                        'Quiz',
                        'Click',
                        `Question ${currentQuestionIndex + 1}. Answer "${label}" (id ${id}, score increment ${currentIncrement})`
                    );
                }

                setTimeout(() => {
                    this.#viewDom.showComment(
                        currentComment,
                        currentQuestionIndex,
                        numQuestions,
                        currentIncrement,
                        isTheLastQuestion ? data.quiz.displayResult : data.quiz.next
                    );
                    this.#view3d.showComment(
                        currentComment,
                        currentQuestionIndex,
                        numQuestions,
                        currentIncrement,
                        isTheLastQuestion ? data.quiz.displayResult : data.quiz.next
                    );
                }, 1500);

                break;
            }
            case QuizActionType.NEXT: {
                this.#model.nextQuestion();
                const {
                    currentQuestionIndex, prevIncrement, complete, totalScore
                } = this.#model;

                if (complete) {
                    const result = this.#model.getResult();
                    const { direction, title } = result;

                    this.#viewDom.hideScore();
                    this.#view3d.hideScore();
                    this.#viewDom.hideQuestion(direction);
                    this.#view3d.hideQuestion(direction);
                    this.#viewDom.showResult(result, data.quiz.restart, direction);
                    this.#view3d.showResult(result, data.quiz.restart, direction);
                    this.#updateShare(result.meta);

                    analyticsEvent('Quiz', 'Click', `Result "${title}"`);
                    analyticsEvent('Quiz', 'Click', `Score ${totalScore}`);

                    break;
                }
                this.#showCurrentQuestion(currentQuestionIndex, numQuestions, prevIncrement);

                analyticsEvent('Quiz', 'Click', `Next - ${currentQuestionIndex + 1}`);

                break;
            }
            case QuizActionType.SHARE: {
                shareFunc(action.social);
                analyticsEvent('Share', 'Click', action.label);

                break;
            }
        }

        this.#updateViewData(isReset);
    }

    #showCurrentQuestion(currentQuestionIndex, numQuestions, prevIncrement) {
        const {
            currentQuestion
        } = this.#model;

        this.#viewDom.showQuestion(currentQuestion, currentQuestionIndex, numQuestions, prevIncrement);
        this.#view3d.showQuestion(currentQuestion, currentQuestionIndex, numQuestions, prevIncrement);
    }

    #updateViewData(immediate) {
        const { totalScore, currentIncrement } = this.#model;

        this.#viewDom.updateScore(totalScore, currentIncrement, immediate);
        this.#view3d.updateScore(totalScore, currentIncrement, immediate);
    }

    // eslint-disable-next-line class-methods-use-this
    #updateShare(newMeta) {
        console.log('#updateShare', newMeta);
        updateShareOptions(newMeta);
    }
}
